import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
} from "../../../Common";

import {
  CheckoutModel_MyBag,
  CheckoutModel_MyBag_Graphql_Request,
} from "../../model";

import { useGenericDataPortForMyBag } from "../use-generic-data-port-for-mybag";

import { DataMutationMyBag_ItemChangeQuantity_QueryKey } from "./data-mutation--mybag-item-change-quantity-query-key-factory";

export interface DataMutationMyBagItemChangeQuantityConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<
    DataMutationMyBagItemChangeQuantity_Result["data"]
  > {
  data: DataMutationMyBagItemChangeQuantity_Result["data"];
  request: DataMutationMyBagItemChangeQuantity_Result["variables"];
  status: DataMutationMyBagItemChangeQuantity_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationMyBagItemChangeQuantityConsumerInterface;
const DataMutationMyBagItemChangeQuantityContext =
  React.createContext(defaultValue);

export interface DataMutationMyBagItemChangeQuantity_ControllerInterface {
  mutate: DataMutationMyBagItemChangeQuantity_Result["mutate"];
  mutateAsync: DataMutationMyBagItemChangeQuantity_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationMyBagItemChangeQuantity_ControllerInterface;
const DataMutationMyBagItemChangeQuantity_ControllerContext =
  React.createContext(controllerDefaultValue);

export interface DataMutationMyBagItemChangeQuantity_Request {
  cartId: string;
  cartItemId_toChange: string;
  newQuantity: number;
}
export type DataMutationMyBagItemChangeQuantity_Result =
  ReactQuery.UseMutationResult<
    CheckoutModel_MyBag,
    unknown,
    DataMutationMyBagItemChangeQuantity_Request,
    unknown
  >;

export type GraphqlResponse_MyBagUpdateCartItems = GraphqlWrappedData<
  "updateCartItems",
  { cart: CheckoutModel_MyBag }
>;

export interface DataMutationMyBagItemChangeQuantityProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationMyBagItemChangeQuantityProvider: React.FunctionComponent<
  DataMutationMyBagItemChangeQuantityProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const [genericDataPortValues, genericDataPortController] =
    useGenericDataPortForMyBag();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutationMyBag_ItemChangeQuantity_QueryKey.mybagItemChangeQuantity();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    CheckoutModel_MyBag,
    DataMutationMyBagItemChangeQuantity_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = `
        mutation {
          updateCartItems(
            input: {
                cart_id: "${request.cartId}",
                cart_items: [
                    {
                      cart_item_id: ${request.cartItemId_toChange}
                      quantity: ${request.newQuantity}
                    }
                ]
            }
          ) {
            cart {
              ${CheckoutModel_MyBag_Graphql_Request}
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponse_MyBagUpdateCartItems>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      genericDataPortController.portError(data);

      const { data: gqlResponseData } = data || {};
      const { updateCartItems } = gqlResponseData || {};
      const { cart } = updateCartItems || {};

      if ((window as any).getMiniCart as any) {
        ((window as any).getMiniCart as any)((customerData: any) => {
          const cart = customerData.get("cart");
          customerData.reload(["cart"], true);
        });
      }

      genericDataPortController.portSuccess();

      return cart;
    },
    [initialState, genericDataPortController]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: genericDataPortController.useMutationOrQueryOnSuccess,
  });

  const value: DataMutationMyBagItemChangeQuantityConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        request: mutation.variables,
        status: mutation.status,
        error: mutation.error,

        dataUpdatedAt: genericDataPortValues.dataUpdatedAt,
        errorUpdatedAt: genericDataPortValues.errorUpdatedAt,
      }),
      [
        mutation.data,
        mutation.variables,
        mutation.status,
        mutation.error,

        genericDataPortValues.dataUpdatedAt,
        genericDataPortValues.errorUpdatedAt,
      ]
    );
  const controllerValue: DataMutationMyBagItemChangeQuantity_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationMyBagItemChangeQuantityContext.Provider value={value}>
      <DataMutationMyBagItemChangeQuantity_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationMyBagItemChangeQuantity_ControllerContext.Provider>
    </DataMutationMyBagItemChangeQuantityContext.Provider>
  );
};

export const useDataMutationMyBagItemChangeQuantity = () => {
  const context = React.useContext(DataMutationMyBagItemChangeQuantityContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationMyBagItemChangeQuantity must be used within a DataMutationMyBagItemChangeQuantityProvider"
    );
  }
  return context;
};

export const useDataMutationMyBagItemChangeQuantity_asController = () => {
  const context = React.useContext(
    DataMutationMyBagItemChangeQuantity_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationMyBagItemChangeQuantity_asController must be used within a DataMutationMyBagItemChangeQuantityProvider"
    );
  }
  return context;
};
